import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, ChevronLeft, Lock, RefreshCw, SlidersHorizontal, Users } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ComponentType, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { CIRCLE_SECTION_LABELS, type CircleSection } from '../../shared/sections'
import { USE_SECTIONS, type UseSection, type Uses } from '../../shared/uses'
import { Choices } from '@/components/Choice'
import { FormError } from '@/components/FormError'
import { ProfilePhoto } from '@/components/ProfilePhoto'
import { MomentsIcon, PhotosIcon, RecipesIcon, type PlaceIconProps } from '@/components/nav/icons'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LogoMark } from '@/components/Wordmark'
import { api, type Me } from '@/lib/api'
import { signOut } from '@/lib/signOut'
import type { WaitingInvite } from '@/lib/invites'
import { useMe } from '@/lib/useMe'
import { cn } from '@/lib/utils'
import { PageLoader } from '@/components/Loader'
import { reducedMotion } from '@/lib/motion'

// The first sign-in, before there's a circle or your own space: a quick profile (your name, and what you're here
// for), anyone who has already invited your email, and then circles, which are optional. Making one explains what a
// circle is and that it can be changed any time; skipping makes your own space ("Me"). It shows until one of those
// exists, so leaving halfway picks up where the account is.
type Step = 'profile' | 'invites' | 'circles' | 'create'

const INTERESTS: { key: UseSection; icon: ComponentType<PlaceIconProps>; line: string }[] = [
  { key: 'photos', icon: PhotosIcon, line: 'Albums, old and new, and the faces in them' },
  { key: 'moments', icon: MomentsIcon, line: 'Stories and memories, written down' },
  { key: 'recipes', icon: RecipesIcon, line: 'Family recipes, card and all' },
]

export function Onboarding() {
  const me = useMe().data!
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const invites = useQuery({
    queryKey: ['invites'],
    queryFn: () => api<{ invites: WaitingInvite[] }>('/invites'),
  })
  const offered = INTERESTS.filter((x) => me.sections[x.key])
  const [step, setStepTo] = useState<Step>('profile')
  // Which way the steps are going, so the next one comes in from that side.
  const [backwards, setBackwards] = useState(false)
  // Where it goes once the last step is done, after a moment's welcome.
  const [arriving, setArriving] = useState<string | null>(null)
  const [name, setName] = useState(me.name)
  const [picked, setPicked] = useState<UseSection[]>(() =>
    offered.filter((x) => me.uses?.[x.key] !== 'off').map((x) => x.key),
  )
  if (invites.isPending) return <PageLoader className="min-h-svh" />
  const waiting = invites.data?.invites ?? []
  const steps: Step[] = ['profile', ...(waiting.length > 0 ? ['invites' as const] : []), 'circles']
  const at = steps.indexOf(step === 'create' ? 'circles' : step)
  const back = step === 'create' ? 'circles' : at > 0 ? steps[at - 1] : null
  const order: Step[] = [...steps, 'create']
  const setStep = (next: Step) => {
    setBackwards(order.indexOf(next) < order.indexOf(step))
    setStepTo(next)
  }

  // Leaving for the app: wherever it goes, then the account as it is now (fetched after, since a circle in the
  // account ends these steps). Into a circle or your own space, a moment's welcome first.
  async function finish(to: string) {
    setArriving(null)
    // These steps can be scrolled down; the app opens at its top.
    window.scrollTo({ top: 0, behavior: 'instant' })
    navigate(to)
    await Promise.all(
      ['me', 'families', 'circles', 'share-places'].map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
    )
  }

  return (
    <div className="min-h-svh overflow-x-clip bg-background">
      <div className="mx-auto flex min-h-svh w-full max-w-md flex-col px-6 pt-[max(env(safe-area-inset-top),1.25rem)] pb-[max(env(safe-area-inset-bottom),1.5rem)]">
        <header className="grid h-11 grid-cols-[5rem_1fr_5rem] items-center">
          {back ? (
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full"
              aria-label="Back"
              onClick={() => setStep(back)}
            >
              <ChevronLeft aria-hidden />
            </Button>
          ) : (
            <span />
          )}
          <LogoMark label className="h-7 justify-self-center" />
          <Button
            variant="ghost"
            size="sm"
            className="justify-self-end rounded-full text-muted-foreground"
            onClick={() => void signOut()}
          >
            Sign out
          </Button>
        </header>
        <ol className="ob-dots mt-4 justify-center" aria-label={`Step ${at + 1} of ${steps.length}`}>
          {steps.map((x, i) => (
            <li key={x} data-done={i < at || undefined} data-here={i === at || undefined} />
          ))}
        </ol>
        <main key={step} data-back={backwards || undefined} className="ob-step flex flex-1 flex-col pt-10">
          {step === 'profile' && (
            <Profile
              me={me}
              offered={offered}
              name={name}
              onName={setName}
              picked={picked}
              onPicked={setPicked}
              onDone={() => setStep(steps[1]!)}
            />
          )}
          {step === 'invites' && (
            <Invites
              invites={waiting}
              email={me.email}
              onOpen={(id) => void finish(`/join/${id}`)}
              onNext={() => setStep('circles')}
            />
          )}
          {step === 'circles' && (
            <Circles onCreate={() => setStep('create')} onJustMe={(id) => setArriving(`/circle/${id}`)} />
          )}
          {step === 'create' && <CreateCircle me={me} picked={picked} onMade={(id) => setArriving(`/circle/${id}`)} />}
        </main>
      </div>
      {arriving && <Welcome name={name} circle={step === 'create'} onDone={() => void finish(arriving)} />}
    </div>
  )
}

// The moment between the last step and the app: three circles drift in and gather into one, the heart appears where
// they meet, and the welcome is written under it. Then the app. A tap goes straight on.
function Welcome({ name, circle, onDone }: { name: string; circle: boolean; onDone: () => void }) {
  const [leaving, setLeaving] = useState(false)
  const done = useRef(onDone)
  useEffect(() => {
    done.current = onDone
  })
  useEffect(() => {
    const simple = reducedMotion()
    const hold = window.setTimeout(() => setLeaving(true), simple ? 600 : 1700)
    const go = window.setTimeout(() => done.current(), simple ? 750 : 2050)
    return () => {
      clearTimeout(hold)
      clearTimeout(go)
    }
  }, [])
  const first = name.trim().split(/\s+/)[0]
  return (
    <div
      role="status"
      className="ob-finale cursor-pointer"
      data-leaving={leaving || undefined}
      onClick={() => done.current()}
    >
      <div className="flex flex-col items-center gap-6 px-6 text-center">
        <div className="ob-finale-mark" aria-hidden="true">
          <span />
          <span />
          <span />
          <LogoMark className="h-16" />
        </div>
        <div className="ob-finale-words flex flex-col gap-2">
          <p className="eyebrow">{circle ? 'Your circle is ready' : 'Your space is ready'}</p>
          <p className="text-display font-semibold tracking-tight">Welcome, {first}.</p>
        </div>
      </div>
    </div>
  )
}

function Title({ eyebrow, title, children }: { eyebrow: string; title: string; children?: ReactNode }) {
  return (
    <header className="flex flex-col gap-3">
      <p className="eyebrow">{eyebrow}</p>
      <h1 className="text-display text-balance">{title}</h1>
      {children && <p className="text-pretty text-muted-foreground">{children}</p>}
    </header>
  )
}

// At the bottom of the screen, where a thumb is, below everything on a longer step.
const Footer = ({ children }: { children: ReactNode }) => (
  <div className="mt-auto flex flex-col gap-2 pt-2">{children}</div>
)

// Your name, and what you're here for: "What I use" in your profile, where it can be changed any time.
function Profile({
  me,
  offered,
  name,
  onName,
  picked,
  onPicked,
  onDone,
}: {
  me: Me
  offered: typeof INTERESTS
  name: string
  onName: (name: string) => void
  picked: UseSection[]
  onPicked: (picked: UseSection[]) => void
  onDone: () => void
}) {
  const queryClient = useQueryClient()
  const save = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim()
      if (trimmed !== me.name) await api('/me', { method: 'PATCH', body: { name: trimmed } })
      const uses = { ...me.uses } as Uses
      for (const section of USE_SECTIONS) {
        if (!offered.some((x) => x.key === section)) continue
        const now = me.uses?.[section] ?? 'post'
        const want = picked.includes(section) ? (now === 'off' ? 'post' : now) : 'off'
        if (want !== now) await api('/me/uses', { method: 'PATCH', body: { section, use: want } })
        uses[section] = want
      }
      // Kept in place rather than fetched again, so these steps carry on where they are.
      queryClient.setQueryData<Me>(['me'], (old) => old && { ...old, name: trimmed, uses })
    },
    onSuccess: onDone,
  })
  const toggle = (key: UseSection) =>
    onPicked(picked.includes(key) ? picked.filter((x) => x !== key) : [...picked, key])

  function submit(e: FormEvent) {
    e.preventDefault()
    save.mutate()
  }

  return (
    <form onSubmit={submit} className="flex flex-1 flex-col gap-10">
      <Title eyebrow="Welcome to gatheredfrom" title="Let’s set up your profile">
        A few quick things, and you’re in.
      </Title>
      <ProfilePhoto me={me} />
      <div className="grid gap-2">
        <Label htmlFor="onboarding-name">Your name</Label>
        <Input
          id="onboarding-name"
          autoComplete="name"
          required
          maxLength={80}
          className="h-12 text-base"
          value={name}
          onChange={(e) => onName(e.target.value)}
        />
        <p className="text-sub text-muted-foreground">It’s shown beside what you add.</p>
      </div>
      {offered.length > 0 && (
        <fieldset className="grid gap-3">
          <legend className="mb-1 font-medium">What are you here for?</legend>
          {offered.map(({ key, icon: Icon, line }) => (
            <Tile
              key={key}
              type="checkbox"
              checked={picked.includes(key)}
              onChange={() => toggle(key)}
              icon={<Icon className="size-6" />}
              title={CIRCLE_SECTION_LABELS[key]}
              line={line}
            />
          ))}
          <p className="text-sub text-muted-foreground">
            Change this any time in your profile.
            {offered.some((x) => x.key !== 'moments') && ' Albums and recipes can become printed books and cards, too.'}
          </p>
        </fieldset>
      )}
      <Footer>
        <FormError error={save.error} />
        <Button
          type="submit"
          size="lg"
          className="h-12 rounded-full text-base"
          disabled={save.isPending || !name.trim() || (offered.length > 0 && picked.length === 0)}
        >
          Continue
        </Button>
      </Footer>
    </form>
  )
}

// Anyone who has already invited your email: the closest the web gets to "see who's here from your contacts", without
// ever looking anyone up.
function Invites({
  invites,
  email,
  onOpen,
  onNext,
}: {
  invites: WaitingInvite[]
  email: string
  onOpen: (id: string) => void
  onNext: () => void
}) {
  return (
    <div className="flex flex-1 flex-col gap-10">
      <Title eyebrow="People you know" title={invites.length === 1 ? 'You’re invited' : 'You have invites'}>
        Someone already saved a place for <span className="break-all">{email}</span>.
      </Title>
      <ul className="grid gap-3" aria-label="Invites">
        {invites.map((x) => (
          <li key={x.id} className="flex items-center gap-4 rounded-2xl border bg-card p-4">
            <Avatar person={x.person ?? { id: x.id, name: x.family, portrait: null }} className="size-12 text-lg" />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-semibold tracking-tight">{x.family}</span>
              <span className="text-sub text-muted-foreground">{x.from} invited you.</span>
            </span>
            <Button asChild className="rounded-full">
              <Link
                to={`/join/${x.id}`}
                aria-label={`Open the invite to ${x.family}`}
                onClick={(e) => {
                  e.preventDefault()
                  onOpen(x.id)
                }}
              >
                Open
              </Link>
            </Button>
          </li>
        ))}
      </ul>
      <Footer>
        <Button variant="ghost" size="lg" className="h-12 rounded-full text-base" onClick={onNext}>
          Not now
        </Button>
      </Footer>
    </div>
  )
}

// Circles are optional: what one is, in a line, and the choice.
function Circles({ onCreate, onJustMe }: { onCreate: () => void; onJustMe: (id: string) => void }) {
  const name = useId()
  const [choice, setChoice] = useState<'circle' | 'me'>('circle')
  const justMe = useMutation({
    mutationFn: () => api<{ id: string }>('/families/personal', { method: 'POST' }),
    onSuccess: ({ id }) => onJustMe(id),
  })
  return (
    <div className="flex flex-1 flex-col gap-10">
      <Title eyebrow="Circles · Optional" title="Gather your people">
        A circle is a small, private group you share with: your family, a few close friends, a team or a club.
      </Title>
      <fieldset className="grid gap-3">
        <legend className="sr-only">How do you want to start?</legend>
        <Tile
          type="radio"
          name={name}
          checked={choice === 'circle'}
          onChange={() => setChoice('circle')}
          icon={<Users className="size-6" strokeWidth={1.75} />}
          title="Create a circle"
          line="Share with family or friends"
        />
        <Tile
          type="radio"
          name={name}
          checked={choice === 'me'}
          onChange={() => setChoice('me')}
          icon={<Lock className="size-6" strokeWidth={1.75} />}
          title="Just me for now"
          line="Keep things in your own space, and add a circle whenever you’re ready"
        />
      </fieldset>
      <Footer>
        <FormError error={justMe.error} />
        <Button
          size="lg"
          className="h-12 rounded-full text-base"
          disabled={justMe.isPending}
          onClick={() => (choice === 'circle' ? onCreate() : justMe.mutate())}
        >
          {choice === 'circle' ? 'Next' : 'Start in my own space'}
        </Button>
      </Footer>
    </div>
  )
}

// Making the circle: what it is, what it shares (starting from what you're here for), and that all of it can change.
function CreateCircle({ me, picked, onMade }: { me: Me; picked: UseSection[]; onMade: (id: string) => void }) {
  const offered = (['photos', 'moments', 'recipes', 'calendar'] as const).filter((s) => me.sections[s])
  const [shares, setShares] = useState<CircleSection[]>(() => {
    const start = offered.filter((s) => s === 'calendar' || picked.includes(s as UseSection))
    return start.length > 0 ? start : [...offered]
  })
  const [tree, setTree] = useState(false)
  const [name, setName] = useState('')
  const sections = [...shares.filter((s) => offered.includes(s as never)), ...(tree ? ['people' as const] : [])]
  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/families', { method: 'POST', body: { name: name.trim(), sections, bring: [] } }),
    onSuccess: ({ id }) => onMade(id),
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    create.mutate()
  }

  return (
    <form onSubmit={submit} className="flex flex-1 flex-col gap-10">
      <Title eyebrow="Circles" title="Create your circle" />
      <ul className="grid gap-4 text-sub text-muted-foreground">
        <Point icon={Users}>Only the people you invite are in it, and they see only what’s shared with it.</Point>
        <Point icon={SlidersHorizontal}>Pick what it keeps track of: photos, moments, recipes, a calendar.</Point>
        <Point icon={RefreshCw}>
          You and your circle can change any of this, any time, in Circle settings, so you share and connect the way you
          like.
        </Point>
      </ul>
      <div className="grid gap-2">
        <Label htmlFor="circle-name">Circle name</Label>
        <Input
          id="circle-name"
          required
          maxLength={80}
          className="h-12 text-base"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <Choices<CircleSection>
        legend="What will you share?"
        hint="You can add more any time."
        value={shares}
        options={offered.map((s) => [s, CIRCLE_SECTION_LABELS[s]])}
        onChange={setShares}
      />
      {me.sections.people && (
        <Choices<'tree'>
          legend="Family tree"
          hint="For a family: a tree to walk together, a page for each home, and memorial pages."
          value={tree ? ['tree'] : []}
          options={[['tree', 'Add a family tree']]}
          onChange={(v) => setTree(v.length > 0)}
        />
      )}
      <Footer>
        <FormError error={create.error} />
        <Button
          type="submit"
          size="lg"
          className="h-12 rounded-full text-base"
          disabled={create.isPending || sections.length === 0 || !name.trim()}
        >
          Create circle
        </Button>
      </Footer>
    </form>
  )
}

function Point({ icon: Icon, children }: { icon: ComponentType<{ className?: string }>; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="grid size-8 shrink-0 place-items-center rounded-full bg-secondary text-foreground">
        <Icon className="size-4" />
      </span>
      <span className="pt-1.5">{children}</span>
    </li>
  )
}

// A big, tappable choice: an icon, a title and a line under it.
function Tile({
  type,
  name,
  checked,
  onChange,
  icon,
  title,
  line,
}: {
  type: 'radio' | 'checkbox'
  name?: string
  checked: boolean
  onChange: () => void
  icon: ReactNode
  title: string
  line: string
}) {
  return (
    <label
      data-press
      className="group flex cursor-pointer items-center gap-4 rounded-2xl border border-border p-4 transition-[background-color,border-color,scale] duration-200 hover:bg-secondary has-checked:border-brand has-checked:bg-secondary has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50 motion-reduce:transition-none"
    >
      <input type={type} name={name} className="sr-only" checked={checked} onChange={onChange} />
      <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-background text-foreground shadow-paper transition-[scale,color] duration-300 ease-settle group-has-checked:scale-[1.08] group-has-checked:text-brand motion-reduce:transition-none">
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="font-semibold tracking-tight">{title}</span>
        <span className="text-sub text-muted-foreground">{line}</span>
      </span>
      <span
        aria-hidden="true"
        className={cn(
          'grid size-6 shrink-0 place-items-center border-2 transition-colors',
          type === 'radio' ? 'rounded-full' : 'rounded-md',
          checked ? 'border-brand bg-brand text-brand-foreground' : 'border-input',
        )}
      >
        {checked && <Check className="size-3.5" strokeWidth={3} />}
      </span>
    </label>
  )
}
